import { useCallback, useEffect, useMemo, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { shortMonthAndYear } from "@similarweb/pro-logic-components";

import { useAsyncHref } from "app/components/hooks/useAsyncHref";
import { NotableChangesCard } from "app/components/insight-ribbon/NotableChangesCard";
import { rankNotableChanges } from "app/components/insight-ribbon/significance";
import type { IInsightRibbonState, INotableChange } from "app/components/insight-ribbon/types";
import { useConversionRateData } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/ConversionRate/useConversionRateData";
import { useConversionRateAvailability } from "app/pages/website-analysis/TrafficAndEngagement/ConversionsOverTime/useConversionRateAvailability";
import { useTopCountriesData } from "app/pages/website-performance/components/TopCountries/useTopCountriesData";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import {
    toChannelNotableChanges,
    toConversionRateNotableChange,
    toGeoNotableChanges,
} from "app/pages/website-performance/insight-ribbon/notableChangesAdapters";
import {
    getMonthOverMonthWindow,
    useTrafficChannelsPopData,
} from "app/pages/website-performance/insight-ribbon/useTrafficChannelsPopData";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { useTrack } from "pro-features/components/WithTrack/src/useTrack";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const TRACKING_CATEGORY = "Insight Ribbon";

const TRANSLATION_KEYS = {
    conversionRate: "insight_ribbon.shared.notable_changes_card.conversion_rate",
    marketingChannelsLink: "insight_ribbon.shared.notable_changes_card.link.marketing_channels",
    geographyLink: "insight_ribbon.shared.notable_changes_card.link.geography",
    conversionRateLink: "insight_ribbon.shared.notable_changes_card.link.conversion_rate",
};

export const WebsitePerformanceInsightRibbon = () => {
    const { translate } = useTranslate();
    const [track] = useTrack();

    const params = proNavigator.getParams();
    const { isWWWithoutPermissions } = websiteWorldWideOverviewLogic(params.country);
    const duration = isWWWithoutPermissions ? "3m" : params.duration;

    const durationObject = DurationService.getDurationData(duration);
    const apiParams = useMemo(() => proNavigator.getApiParams({ ...params, duration }), [params, duration]);

    // A 28-day window has no monthly baseline, so every source returns a null change.
    const isComparisonUnavailable = !!apiParams.isWindow;

    const channelsController = isWWWithoutPermissions
        ? "WorldwideOverviewTotal/TrafficSourcesTotalOverview"
        : "MarketingMixTotal/TrafficSourcesOverview";

    const monthOverMonthWindow = useMemo(() => getMonthOverMonthWindow(durationObject.raw.to), [durationObject.raw.to]);

    // Built from the raw route duration rather than the (possibly forced) ribbon duration so this shares
    // one cached Firebolt query with the geography widget. Both resolve to the same month-over-month
    // baseline either way, because every preset ends on the latest supported month.
    const geographyRange = DurationService.getDurationData(params.duration).forAPI;

    const {
        data: countries,
        isLoading: isLoadingCountries,
        error: countriesError,
    } = useTopCountriesData(
        {
            keys: apiParams.key,
            webSource: params.webSource,
            includeSubDomains: params.isWWW === "*",
            from: geographyRange.from,
            to: geographyRange.to,
            isWindow: geographyRange.isWindow,
        },
        { enabled: !isComparisonUnavailable },
    );

    const {
        data: channels,
        isLoading: isLoadingChannels,
        error: channelsError,
    } = useTrafficChannelsPopData(
        {
            keys: apiParams.key,
            country: params.country,
            includeSubDomains: params.isWWW === "*",
            ...monthOverMonthWindow,
        },
        channelsController,
        { enabled: !isComparisonUnavailable },
    );

    const conversionRateAvailability = useConversionRateAvailability({
        keys: [apiParams.key],
        from: apiParams.from,
        to: apiParams.to,
        isWWW: apiParams.isWWW,
    });
    // While the ecommerce probe runs the hook reports the permission state, which is "available" even for
    // domains that turn out to have no conversion data.
    const isConversionRateAvailable =
        !conversionRateAvailability.isLoading && conversionRateAvailability.state === "available";

    const { data: conversionRates, isLoading: isLoadingConversionRate } = useConversionRateData(
        {
            country: apiParams.country,
            from: apiParams.from,
            to: apiParams.to,
            keys: [apiParams.key],
            isWindow: apiParams.isWindow,
            includeSubdomains: !apiParams.isWWW,
        },
        { enabled: isConversionRateAvailable && !isComparisonUnavailable },
    );

    const channelsHref = useAsyncHref("digitalsuite_website_marketingchannels", params);
    const geographyHref = useAsyncHref("digitalsuite_website_geography", params);
    const conversionRateHref = useAsyncHref("digitalsuite_website_trafficandengagement", params);

    const deepLinks = useMemo(
        () => ({
            channels: { href: channelsHref, textKey: TRANSLATION_KEYS.marketingChannelsLink },
            geography: { href: geographyHref, textKey: TRANSLATION_KEYS.geographyLink },
            conversionRate: { href: conversionRateHref, textKey: TRANSLATION_KEYS.conversionRateLink },
        }),
        [channelsHref, geographyHref, conversionRateHref],
    );

    const notableChanges = useMemo(() => {
        const conversionRate = conversionRates?.[0];

        return rankNotableChanges([
            ...toChannelNotableChanges(channels, deepLinks.channels),
            ...toGeoNotableChanges(countries, deepLinks.geography),
            ...toConversionRateNotableChange(
                conversionRate?.conversionRate,
                conversionRate?.change,
                translate(TRANSLATION_KEYS.conversionRate),
                deepLinks.conversionRate,
            ),
        ]);
    }, [channels, countries, conversionRates, deepLinks, translate]);

    const isLoading =
        !isComparisonUnavailable &&
        (isLoadingCountries ||
            isLoadingChannels ||
            conversionRateAvailability.isLoading ||
            (isConversionRateAvailable && isLoadingConversionRate));

    // Conversion rate is left out: its endpoint also errors for domains without conversion data, and its
    // own widget already reports a genuine failure.
    const hasError = !!countriesError || !!channelsError;

    // Keyed on the query so switching domain or timeframe without unmounting reports a fresh impression.
    const trackingContext = `${apiParams.key}|${duration}`;
    const trackedContext = useRef<string | null>(null);

    useEffect(() => {
        if (isLoading || trackedContext.current === trackingContext) {
            return;
        }

        trackedContext.current = trackingContext;
        const loadedKinds = notableChanges.map(({ kind }) => kind).join("|");
        const outcome = loadedKinds || (hasError ? "error" : "empty");
        track(TRACKING_CATEGORY, "load", `notable changes/${outcome}`);
    }, [isLoading, notableChanges, hasError, track, trackingContext]);

    const onNotableChangeClick = useCallback(
        (change: INotableChange) => track(TRACKING_CATEGORY, "click", `${change.kind}/${change.label}`),
        [track],
    );

    const state: IInsightRibbonState = {
        notableChanges,
        isLoading,
        isComparisonUnavailable,
        hasError,
        dateRangeText: `${durationObject.raw.from.format(shortMonthAndYear)} - ${durationObject.raw.to.format(shortMonthAndYear)}`,
        countryText: CountryService.getCountryById(params.country)?.text ?? "",
        countryCode: Number(params.country),
        onNotableChangeClick,
    };

    // No wrapper element: the cards are laid out as items of the surrounding widget grid.
    return <NotableChangesCard {...state} />;
};
